/**
 * Общая логика отправки супер симпатии — одна на все точки входа: лента
 * (useProfileActions), анкета из уведомлений/чатов (usePeerActions), чужая
 * анкета на странице профиля (ProfilePage) и «Куда сходить» (WhereToGoPage).
 *
 * Раньше профиль и «Куда сходить» слали супер симпатию напрямую, без формы
 * с сообщением и без магазина при нулевом балансе, а лента и анкета при 402
 * теряли набранное сообщение. Здесь — чистые функции без React (DOM-рендерера
 * в юнит-тестах проекта нет), React-обёртка — hooks/useSuperLikeSend.ts.
 */
import type {ApiResponse} from "../services/api";
import type {SuperLikeResponse} from "../services/socialService";

/** Код ошибки бэкенда при 402 «супер симпатии закончились» (social_routes.superlike_user). */
export const SUPERLIKES_EXHAUSTED_CODE = "SUPERLIKES_EXHAUSTED";

export const SUPER_LIKE_SENT_TOAST = "Супер симпатия отправлена";
export const SUPER_LIKE_FAILED_TOAST = "Не удалось отправить супер симпатию. Попробуйте ещё раз.";

/**
 * Ответ — «супер симпатии закончились» (402), а не иная ошибка. Сначала
 * машиночитаемый код; текст — запасной путь для ответа старого бэкенда.
 */
export function isSuperLikesExhausted(result: Pick<ApiResponse<unknown>, "error" | "code">): boolean {
    if (!result.error) return false;
    if (result.code === SUPERLIKES_EXHAUSTED_CODE) return true;
    const message = result.error.toLowerCase();
    return message.includes("нет супер симпатий") || message.includes("купите") || message.includes("подписк");
}

/**
 * Вместо формы сразу магазин пакетов: супер симпатии закончились. Как в ленте
 * (требование продакта 02.09): у Premium клиентский остаток может отстать от
 * нового окна квоты, поэтому для него решает сервер (402 → магазин).
 */
export function shouldOpenShopInsteadOfCompose(economy: {isPremium: boolean; superLikesRemaining: number}): boolean {
    return !economy.isPremium && economy.superLikesRemaining <= 0;
}

/* ──────── Черновик сообщения ────────
 * Форма закрывается в момент нажатия «Отправить», поэтому при ЛЮБОЙ неудаче
 * (402, 409, 5xx, сеть) набранное сообщение сохраняем: при 402 человек уходит
 * в магазин (в том числе на оплату в Т-Банк с перезагрузкой страницы), а
 * потом открывает форму этому же человеку — текст на месте. sessionStorage
 * живёт в пределах вкладки и переживает редирект на оплату.
 *
 * Черновики принадлежат аккаунту: ключ содержит id своего пользователя
 * (другой аккаунт в той же вкладке чужих сообщений не увидит), а при выходе
 * все черновики стираются (clearSuperLikeDraftsOnLogout из tokenStorage.clearTokens). */

export const SUPER_LIKE_DRAFTS_PREFIX = "rl.superLikeDrafts:";

type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type EnumerableStorage = DraftStorage & Pick<Storage, "key" | "length">;

function defaultStorage(): EnumerableStorage | null {
    try {
        return typeof sessionStorage === "undefined" ? null : sessionStorage;
    } catch {
        return null;
    }
}

function draftsKey(ownerId: string): string {
    return `${SUPER_LIKE_DRAFTS_PREFIX}${ownerId}`;
}

function readAll(ownerId: string, storage: DraftStorage | null): Record<string, string> {
    if (!storage || !ownerId) return {};
    try {
        const parsed: unknown = JSON.parse(storage.getItem(draftsKey(ownerId)) || "{}");
        return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
    } catch {
        return {};
    }
}

function writeAll(ownerId: string, storage: DraftStorage | null, drafts: Record<string, string>): void {
    if (!storage || !ownerId) return;
    try {
        if (Object.keys(drafts).length === 0) storage.removeItem(draftsKey(ownerId));
        else storage.setItem(draftsKey(ownerId), JSON.stringify(drafts));
    } catch {
        // Квота/приватный режим: черновик — удобство, не повод ронять отправку.
    }
}

/** `ownerId` — id своего пользователя; без него черновики не читаются и не пишутся. */
export function readSuperLikeDraft(ownerId: string | null | undefined, peerId: string, storage: DraftStorage | null = defaultStorage()): string {
    if (!ownerId) return "";
    const value = readAll(ownerId, storage)[peerId];
    return typeof value === "string" ? value : "";
}

export function saveSuperLikeDraft(ownerId: string | null | undefined, peerId: string, message: string | undefined, storage: DraftStorage | null = defaultStorage()): void {
    if (!ownerId) return;
    const drafts = readAll(ownerId, storage);
    const text = (message ?? "").trim();
    if (text) drafts[peerId] = text;
    else delete drafts[peerId];
    writeAll(ownerId, storage, drafts);
}

export function clearSuperLikeDraft(ownerId: string | null | undefined, peerId: string, storage: DraftStorage | null = defaultStorage()): void {
    saveSuperLikeDraft(ownerId, peerId, undefined, storage);
}

/** Выход из аккаунта: стереть черновики всех аккаунтов этой вкладки. */
export function clearSuperLikeDraftsOnLogout(storage: EnumerableStorage | null = defaultStorage()): void {
    if (!storage) return;
    try {
        const keys: string[] = [];
        for (let i = 0; i < storage.length; i++) {
            const k = storage.key(i);
            if (k && k.startsWith(SUPER_LIKE_DRAFTS_PREFIX)) keys.push(k);
        }
        keys.forEach((k) => storage.removeItem(k));
    } catch {
        /* хранилище недоступно — нечего чистить */
    }
}

/* ──────── Отправка из анкеты ──────── */

export type SuperLikeSendOutcome =
    | {kind: "sent"; data: SuperLikeResponse}
    | {kind: "matched"; data: SuperLikeResponse}
    | {kind: "exhausted"}
    | {kind: "error"; message: string};

export type SuperLikeSendDeps = {
    /** id своего пользователя — владелец черновика. */
    ownerId: string | null | undefined;
    send: (peerId: string, opts: {message?: string}) => Promise<ApiResponse<SuperLikeResponse>>;
    setBalances: (balance: number, premiumLeft?: number | null, periodEnd?: string | null) => void;
    /** Тихо сверить остатки с сервером (после 402 клиентский остаток устарел). */
    resyncBalances: () => void;
    openShop: () => void;
    toastSuccess: (message: string) => void;
    toastError: (message: string) => void;
    storage?: DraftStorage | null;
};

/**
 * Отправить супер симпатию из формы и разобрать ответ одинаково для всех
 * анкет вне ленты (у ленты своя анимация и оптимистичное списание, но те же
 * правила 402 и черновика):
 *  - любая неудача → набранное сообщение сохраняется черновиком;
 *  - 402 → магазин пакетов и сверка остатков (следующий тап — сразу в магазин);
 *  - иная ошибка → тост с текстом сервера;
 *  - успех → баланс из ответа, черновик стёрт; без мэтча — тост «отправлена»,
 *    мэтч оставляем вызывающему (там своя сцена селебрейшна).
 */
export async function sendSuperLikeFromCompose(
    peerId: string,
    note: string | undefined,
    deps: SuperLikeSendDeps,
): Promise<SuperLikeSendOutcome> {
    const storage = deps.storage === undefined ? defaultStorage() : deps.storage;
    let result: ApiResponse<SuperLikeResponse>;
    try {
        result = await deps.send(peerId, {message: note});
    } catch (error) {
        console.warn("Super like failed:", error);
        saveSuperLikeDraft(deps.ownerId, peerId, note, storage);
        deps.toastError(SUPER_LIKE_FAILED_TOAST);
        return {kind: "error", message: SUPER_LIKE_FAILED_TOAST};
    }
    if (result.error || !result.data) {
        saveSuperLikeDraft(deps.ownerId, peerId, note, storage);
        if (result.error && isSuperLikesExhausted(result)) {
            deps.resyncBalances();
            deps.openShop();
            return {kind: "exhausted"};
        }
        const message = result.error || SUPER_LIKE_FAILED_TOAST;
        deps.toastError(message);
        return {kind: "error", message};
    }
    clearSuperLikeDraft(deps.ownerId, peerId, storage);
    if (typeof result.data.superLikesBalance === "number") {
        deps.setBalances(result.data.superLikesBalance, result.data.premiumSuperlikesLeft, result.data.premiumSuperlikesPeriodEnd);
    }
    if (result.data.matched) return {kind: "matched", data: result.data};
    deps.toastSuccess(SUPER_LIKE_SENT_TOAST);
    return {kind: "sent", data: result.data};
}
